import type {
  PortableTextComponentProps,
  PortableTextMarkComponentProps,
  ReactPortableTextList,
} from '@portabletext/react';
import { PortableText, toPlainText } from '@portabletext/react';
import type { PortableTextBlock, PortableTextLink } from '@portabletext/types';
import Link from 'next/link';
import slugify from 'slugify';

import type { LegalPageSellersType } from '#__generated__/sanity';
import { getFormattedDateTime } from '#utils/getFormattedDateTime';

import { Seo } from '../Seo';

type Props = {
  page: LegalPageSellersType;
};

type TextType = PortableTextComponentProps<PortableTextBlock>;
type LinkType = PortableTextMarkComponentProps<PortableTextLink>;
type ListType = PortableTextComponentProps<ReactPortableTextList>;
type TextTypeReturn = (args: TextType) => JSX.Element;
type LinkTypeReturn = (args: LinkType) => JSX.Element;
type ListTypeReturn = (args: ListType) => JSX.Element;

type ComponentsToRender = {
  block: Record<string, TextTypeReturn>;
  marks: Record<string, LinkTypeReturn>;
  list: Record<string, ListTypeReturn>;
};

type TextSection = [string, Array<string>];

const componentsToRender: ComponentsToRender = {
  block: {
    h2: ({ children, value }) => {
      const id = slugify(toPlainText(value));
      return (
        <h2 id={id} className="mb-3 mt-4 text-2xl font-bold md:mb-4 md:mt-8">
          {children}
        </h2>
      );
    },
    h3: ({ children, value }) => {
      const id = slugify(toPlainText(value));
      return (
        <h3 id={id} className="mb-4 font-semibold">
          {children}
        </h3>
      );
    },
    // Design/Product requested to keep all heading with the same size (text-base)
    // because it will never have more than 2 levels (h2/h3)
    // and they confirmed that all editors will be aware.
    h4: ({ children }) => <h4 className="mb-2 font-semibold">{children}</h4>,
    h5: ({ children }) => <h5 className="mb-2 font-semibold">{children}</h5>,
    h6: ({ children }) => <h6 className="mb-2 font-semibold">{children}</h6>,
    normal: ({ children }) => <p className="mb-4">{children}</p>,
  },
  marks: {
    link: ({ value, text }) =>
      value?.href ? (
        <Link href={value.href} className="text-primary-700">
          {text}
        </Link>
      ) : (
        <span>{text}</span>
      ),
  },
  list: {
    bullet: ({ children }) => {
      return <ul className="mb-4 list-disc space-y-1 pl-8">{children}</ul>;
    },
    number: ({ children }) => (
      <ol className="mb-4 list-disc space-y-1 pl-8">{children}</ol>
    ),
  },
};

export const LegalPageTemplate = ({ page }: Props): JSX.Element => {
  const {
    title,
    description,
    pageHeading,
    pageSubheading,
    publishedDate,
    enteredIntoEffectDate,
    content,
  } = page;

  const formattedPublishedDate = getFormattedDateTime(publishedDate);
  const formattedEnteredIntoEffectDate = getFormattedDateTime(
    enteredIntoEffectDate,
  );

  const headingBlocks = content
    .filter((element) => element._type === 'block')
    .filter(({ style }) => style === 'h2' || style === 'h3');

  const headingTexts = headingBlocks.reduce<Array<TextSection>>(
    (array, item) => {
      const itemText = item.children[0].text;
      if (item.style === 'h2' && itemText !== '') array.push([itemText, []]);
      return array;
    },
    [],
  );

  return (
    <>
      <Seo title={title} description={description} />
      <div className="container m-0 mx-auto bg-gray-50 pt-24">
        <div className="px-6 pt-8">
          <div className="max-w-prose text-gray-700">
            <h1 className="q-text-header-4xl mb-3 text-gray-900 lg:text-5xl">
              {pageHeading}
            </h1>
            <p className="q-text-body-base mb-4">{pageSubheading}</p>
            <p className="q-text-body-base text-sm">
              Published On{' '}
              <time dateTime={publishedDate}>{formattedPublishedDate}</time>.
              Entered Into Effect{' '}
              <time dateTime={enteredIntoEffectDate}>
                {formattedEnteredIntoEffectDate}
              </time>
              .
            </p>
          </div>
        </div>
      </div>
      <div className="container m-0 mx-auto grid gap-2 bg-gray-50">
        <div className="relative grid grid-cols-1 gap-2 md:grid-cols-3">
          <div className="col-span-full p-8 px-6 lg:sticky lg:top-0 lg:col-span-1 lg:h-screen lg:self-start lg:overflow-scroll">
            <h3 className="q-text-body-base-bold mb-3 text-gray-900">
              Sections
            </h3>
            <ul className="space-y-2">
              {headingTexts.map(([sectionHeading, sectionItems]) => {
                return (
                  <li key={sectionHeading}>
                    <a
                      href={`#${slugify(sectionHeading)}`}
                      className="text-info-700"
                    >
                      {sectionHeading}
                    </a>
                    {sectionItems?.length ? (
                      <ul className="ml-4">
                        {sectionItems.map((item) => (
                          <li key={item}>
                            <a
                              href={`#${slugify(item)}`}
                              className="text-info-700"
                            >
                              {item}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="col-span-full row-start-2 px-6 pb-48 lg:col-span-2 lg:row-start-1">
            <div className="max-w-prose">
              <PortableText
                value={content}
                components={componentsToRender}
                onMissingComponent={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
